import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Logo, ArrowRight } from '../Login/svg';
import { Card } from '../../components/UI/Card';
import { Input } from '../../components/UI/Input';
import { Button } from '../../components/UI/Button';
import { Alert } from '../../components/UI/Alert';
import { Loader } from '../../components/UI/Loader';
import SuccessFeedbackIcon from '../../components/UI/SuccessFeedbackIcon';
import AuthService from '../../services/AuthService';
import { hasLetter, hasMinLength, hasNumber } from '../../utils/validatePassword';

import {
  Content,
  ErrorMessage,
  RecoverPasswordButton,
  ResetPasswordContent,
  SubmitButtonWrapper,
  SuccessActions,
  SuccessContainer,
  SuccessContent,
  SuccessMessage,
  Title,
  WarningList,
} from './styles';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const pathParams = params['*'] || '';

  const [userId, setUserId] = useState('');
  const [validationCode, setValidationCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isRequesting, setIsRequesting] = useState(false);
  const [step, setStep] = useState<'otp' | 'password' | 'success'>('otp');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoadingLinkValidation, setIsLoadingLinkValidation] = useState(true);

  const resolvedHashes = useMemo(() => {
    const pathParts = pathParams.split('/').filter(Boolean);
    const query = new URLSearchParams(location.search);

    const emailFromPath = pathParts[0] || '';
    const pwdFromPath = pathParts[1] || '';

    const emailFromQuery = query.get('emailHash') || query.get('email') || query.get('user') || '';
    const pwdFromQuery = query.get('pwdHash') || query.get('hash') || query.get('password') || '';

    return {
      emailHash: decodeURIComponent(emailFromPath || emailFromQuery),
      pwdHash: decodeURIComponent(pwdFromPath || pwdFromQuery),
    };
  }, [location.search, pathParams]);

  useEffect(() => {
    const validateResetLink = async () => {
      try {
        const { emailHash, pwdHash } = resolvedHashes;

        if (!emailHash || !pwdHash) {
          navigate('/?reseterror=true');
          return;
        }

        const id = await AuthService.getValidatePasswordResetLink(emailHash, pwdHash);
        setUserId(id);
      } catch (error) {
        navigate('/?reseterror=true');
      } finally {
        setIsLoadingLinkValidation(false);
      }
    };

    validateResetLink();
  }, [navigate, resolvedHashes]);

  const validateCodeButtonDisabled = validationCode.length !== 6 || isRequesting;
  const hasMinLengthRule = useMemo(() => hasMinLength(password), [password]);
  const hasNumberRule = useMemo(() => hasNumber(password), [password]);
  const hasLetterRule = useMemo(() => hasLetter(password), [password]);
  const passwordsMatch = useMemo(() => !!confirmPassword && password === confirmPassword, [confirmPassword, password]);

  const savePasswordButtonDisabled =
    isRequesting || !hasMinLengthRule || !hasNumberRule || !hasLetterRule || !passwordsMatch;

  const handleValidationCode = (target: any) => {
    setErrorMessage('');
    const onlyNumbers = target.value.replace(/\D/g, '').slice(0, 6);
    setValidationCode(onlyNumbers);
  };

  const handlePasswordChange = (target: any) => {
    setErrorMessage('');
    setPassword(target.value);
  };

  const handleConfirmPasswordChange = (target: any) => {
    setErrorMessage('');
    setConfirmPassword(target.value);
  };

  const handleValidateOtp = async () => {
    try {
      setIsRequesting(true);
      setErrorMessage('');

      const response = await AuthService.getValidateCodeIsValid({
        userId,
        otp: validationCode,
      });

      if (response) {
        setStep('password');
        return;
      }

      setErrorMessage('Código inválido. Tente novamente.');
    } catch (error) {
      setErrorMessage('Código inválido. Tente novamente.');
    } finally {
      setIsRequesting(false);
    }
  };

  const handleSaveNewPassword = async () => {
    try {
      setIsRequesting(true);
      setErrorMessage('');

      await AuthService.putSaveNewPassword({
        password,
        confirmPassword,
        otp: validationCode,
        userId,
        temporaryPassword: resolvedHashes.pwdHash,
      });

      setStep('success');
    } catch (error) {
      setErrorMessage('Não foi possível redefinir a senha. Tente novamente.');
    } finally {
      setIsRequesting(false);
    }
  };

  if (isLoadingLinkValidation) {
    return (
      <ResetPasswordContent>
        <Card>
          <Content>
            <Logo />
            <Title>Validando link...</Title>
            <Loader />
          </Content>
        </Card>
      </ResetPasswordContent>
    );
  }

  return (
    <ResetPasswordContent>
      <Card>
        <Content>
          {step !== 'success' ? <Logo /> : null}

          {step === 'otp' ? (
            <>
              <Title>Validação de código</Title>
              <Alert className="InfoAlert" title="O código de validação foi enviado para seu e-mail." info />
              {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
              <Input
                id="validationCode"
                name="validationCode"
                label="Código de validação"
                type="text"
                onChange={handleValidationCode}
                disabled={isRequesting}
                value={validationCode}
                placeholder="Digite o código"
                maxLength={6}
              />
              <RecoverPasswordButton type="button" onClick={() => navigate('/esqueci-minha-senha')}>
                Redefinir senha novamente
              </RecoverPasswordButton>
              <SubmitButtonWrapper>
                <Button disabled={validateCodeButtonDisabled} black onClick={handleValidateOtp}>
                  {isRequesting ? 'Validando...' : 'Próximo'}
                  {isRequesting ? <Loader /> : <ArrowRight />}
                </Button>
              </SubmitButtonWrapper>
            </>
          ) : null}

          {step === 'password' ? (
            <>
              <Title>Redefinição de senha</Title>
              <Alert className="WarningAlert" title="Sua senha deve conter:" warning>
                <WarningList>
                  <li>No mínimo 6 caracteres</li>
                  <li>Pelo menos um número</li>
                  <li>Pelo menos uma letra</li>
                </WarningList>
              </Alert>
              {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
              <Input
                id="password"
                name="password"
                label="Nova senha"
                type="password"
                isPassword
                onChange={handlePasswordChange}
                disabled={isRequesting}
                value={password}
                placeholder="Digite a nova senha"
                norMarginLeft
              />
              <Input
                id="confirmPassword"
                name="confirmPassword"
                label="Confirme a senha"
                type="password"
                isPassword
                onChange={handleConfirmPasswordChange}
                disabled={isRequesting}
                value={confirmPassword}
                placeholder="Confirme a nova senha"
                norMarginLeft
              />
              {!passwordsMatch && confirmPassword ? <ErrorMessage>As senhas não são iguais.</ErrorMessage> : null}
              <Button disabled={savePasswordButtonDisabled} black onClick={handleSaveNewPassword}>
                {isRequesting ? 'Salvando...' : 'Salvar'}
                {isRequesting ? <Loader /> : <ArrowRight />}
              </Button>
            </>
          ) : null}

          {step === 'success' ? (
            <SuccessContainer>
              <SuccessContent>
                <SuccessFeedbackIcon />
                <SuccessMessage>Senha alterada com sucesso</SuccessMessage>
              </SuccessContent>
              <SuccessActions>
                <Button white onClick={() => navigate('/')}>
                  Acessar Bari Consig
                </Button>
              </SuccessActions>
            </SuccessContainer>
          ) : null}
        </Content>
      </Card>
    </ResetPasswordContent>
  );
};

export default ResetPassword;
